import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hash, Lock, MessageSquareText } from "lucide-react";
import { Avatar } from "./Avatar";
import { api } from "@/lib/api";
import { fmtFull, fmtRelative, plainText } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const cache = new Map();
export const fetchMessage = (mid) => {
  if (!cache.has(mid)) cache.set(mid, api.get(`/messages/${mid}`).then((r) => r.data).catch((e) => { cache.delete(mid); throw e; }));
  return cache.get(mid);
};
export const primeMessage = (m) => {
  if (!m?.id) return;
  const prev = cache.get(m.id);
  if (prev) cache.set(m.id, prev.then((c) => ({ ...c, ...m, conversation: c.conversation || m.conversation })));
  else if (m.conversation) cache.set(m.id, Promise.resolve(m));
};

export const MESSAGE_LINK_RE = /\/client\/(?:dms\/)?([a-f0-9]{24})\?m=([a-f0-9]{24})/;
export const isMessageLink = (url) => { try { const u = new URL(url, window.location.origin); return u.origin === window.location.origin && MESSAGE_LINK_RE.test(u.pathname + u.search); } catch { return false; } };
export const parseMessageLink = (url) => { const u = new URL(url, window.location.origin); const m = (u.pathname + u.search).match(MESSAGE_LINK_RE); return m ? { cid: m[1], mid: m[2] } : null; };

export const MessageUnfurl = ({ cid, mid, sourceId }) => {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { let on = true; fetchMessage(mid).then((d) => on && setM(d)).catch(() => on && setErr(true)); return () => { on = false; }; }, [mid]);
  if (err) return <div className="tc-unfurl is-error" data-testid={`unfurl-${mid}`}>This message isn't available (deleted or in a private conversation).</div>;
  if (!m) return <div className="tc-unfurl is-loading" data-testid={`unfurl-${mid}`}><span className="tc-unfurl__skel" /><span className="tc-unfurl__skel short" /></div>;
  const conv = m.conversation || {};
  const open = () => {
    analytics.track("message_unfurl_clicked", { category: "navigation", target: "message", target_id: mid, conversation_id: cid, metadata: { from_message: sourceId } });
    navigate(`/client/${conv.id || cid}?m=${mid}`);
  };
  const text = plainText(m.text || "");
  return (
    <div className="tc-unfurl" data-testid={`unfurl-${mid}`}>
      <div className="tc-unfurl__head">
        <Avatar user={m.user} size={20} radius={4} />
        <span className="tc-unfurl__author">{m.bot_name || m.user?.display_name}</span>
        <span className="tc-unfurl__time" title={fmtFull(m.created_at)}>{fmtRelative(m.created_at)}</span>
      </div>
      <div className="tc-unfurl__text">{m.subtype === "email" && m.meta?.email ? `✉️ ${m.meta.email.subject} — ` : ""}{text.length > 320 ? `${text.slice(0, 317)}…` : text || (m.files?.length ? `📎 ${m.files.map((f) => f.name).join(", ")}` : "")}</div>
      <div className="tc-unfurl__foot">
        <span className="tc-unfurl__where">{conv.type === "channel" ? <>{conv.is_private ? <Lock size={12} /> : <Hash size={12} />} {conv.name}</> : <><MessageSquareText size={12} /> {conv.display_name || "Direct message"}</>}</span>
        {m.parent_id && <span className="muted">· in a thread</span>}
        <button className="tc-unfurl__view" onClick={open} data-testid={`unfurl-view-${mid}`}>View message</button>
      </div>
    </div>
  );
};
